import React from "react";
import BannerOverlay from "../BannerOverlay/BannerOverlay";
import ImageComponent from "@project-stories/elements/Image/Image";
import { sendGtagEvent } from "frontend/js/helpers";

const BannerImage = ({ blockItem, section_settings, isMobile, isLazy, isHero = false, isFirstSlide = false }) => {
  console.log('blockItem.settings.background_size:', blockItem.settings.background_size);
  let imageUrl = blockItem.settings.image?.src ?? blockItem.settings.image;

  let imageHeight = blockItem.image_height;
  let imageWidth = blockItem.image_width;

  if (isMobile) {
    imageUrl = blockItem.settings.mobile_image
      ? blockItem.settings.mobile_image
      : blockItem.settings.image?.src ?? blockItem.settings.image;

      imageHeight = blockItem.mobile_image_height;
      imageWidth = blockItem.mobile_image_width;

  }
  
  // Determine if this image should be prioritized (hero banner first slide)
  const shouldPrioritize = isHero || isFirstSlide;
  const actuallyLazy = shouldPrioritize ? false : isLazy;

  // Get focal points and background size settings
  const focalPointX = blockItem.image_focal_points && blockItem.image_focal_points.x
    ? `${blockItem.image_focal_points.x}%`
    : "center";

  const focalPointY = blockItem.image_focal_points && blockItem.image_focal_points.y
    ? `${blockItem.image_focal_points.y}%`
    : "center";

  // Treat "background_size" as object-fit intent (cover/contain). Default to contain to prevent cropping.
  const objectFit: React.CSSProperties["objectFit"] =
    blockItem.settings.background_size === "cover" ? "cover" : "contain";
  
  // Determine object position for image
  const objectPosition = `${focalPointX} ${focalPointY}`;
  
  // Build an aspect-style container to maintain proper aspect ratio across all breakpoints
  const aspectStyle: React.CSSProperties = {
    position: "relative",
    width: "100%",
    // Let aspect-ratio control the height; do not force height:auto (can lead to inconsistent sizing)
    ...(imageWidth && imageHeight
      ? { aspectRatio: `${imageWidth}/${imageHeight}` }
      : (blockItem.settings.background_size === "cover" ? { minHeight: "100vh" } : {}) // Fallback minimum height when dimensions aren't available
    ),
  };

  const ImageContent = ({isLazy = true}) => (
    <div className="absolute inset-0 w-full h-full">
      <ImageComponent
        imageUrl={imageUrl}
        alt={blockItem.image_alt || blockItem.settings.title || 'Banner image'}
        classes={`w-full h-full ${blockItem.settings.background_size === "cover" ? "object-cover" : "!object-contain !relative"}`}
        style={{ objectPosition, objectFit: blockItem.settings.background_size === "cover" ? "cover" : "contain" }}
        isLazy={isLazy && !shouldPrioritize}
        useSrcSet={true}
        sizes="(max-width: 640px) 100vw, 100vw"
        priority={shouldPrioritize}
        height={imageHeight}
        width={imageWidth}
      />
    </div>
  );

  function handleClick (e, blockItem) {
    e.preventDefault();

    sendGtagEvent(
      'select_promotion',
      blockItem?.id,
      blockItem?.settings?.promotional_name,
      'banner',
      blockItem?.settings?.button_url
    );
  }

  function handleSecondButtonClick(e, blockItem) {
    e.preventDefault();
    sendGtagEvent(
      'select_promotion',
      blockItem?.id,
      blockItem?.settings?.promotional_name,
      'banner_second_button',
      blockItem?.settings?.button_url_2
    );
  }

  // Calculate aspect ratio with fallback to 16:9 for hero banners
  let aspectRatio: number = 16 / 9;
  let aspectRatioString = '16 / 9';
  
  if (imageWidth && imageHeight && imageWidth > 0 && imageHeight > 0) {
    aspectRatio = imageWidth / imageHeight;
    aspectRatioString = `${imageWidth} / ${imageHeight}`;
  }

  // Mobile needs different handling for hero banners
  const styles = isMobile ? {
    minHeight: '100vh',
    aspectRatio: 'unset'
  } : {
    minHeight: '100vh',
    aspectRatio: aspectRatioString
  };

  return (
    <div
      className={`banner-image ${blockItem.settings.darken_image && "content-over-media"} w-full relative`}
      style={{
        ...styles,
        width: '100%',
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      {/* Fallback for browsers without aspect-ratio support - only for desktop */}
      {!isMobile && (
        <div 
          style={{
            width: '100%',
            paddingTop: `${(1 / aspectRatio) * 100}%`,
            pointerEvents: 'none',
            fontSize: 0,
            position: 'absolute',
            inset: 0,
            zIndex: 0,
          }} 
          aria-hidden="true" 
        />
      )}
      <ImageContent isLazy={actuallyLazy}/>
    </div>
  );
};

export default BannerImage;
